import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car,
  CheckCircle2,
  Clock,
  Inbox,
  Plus,
  Edit,
  Trash2,
  Phone,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { api, BRANDS, FUEL_TYPES, TRANSMISSIONS, CITIES, INR, KM, formatApiError } from "../lib/gj";
import { useAuth } from "../lib/auth";

const empty = {
  brand: "Maruti",
  model: "",
  year: 2022,
  fuel_type: "Petrol",
  transmission: "Manual",
  km_driven: 0,
  price: 0,
  location: "Kolkata",
  description: "",
  images: [],
  owners: 1,
  color: "",
  phone: "",
};

function CarForm({ initial, onSave, onClose }) {
  const [form, setForm] = useState(initial || empty);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const readers = files.map(
      (f) =>
        new Promise((resolve) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.readAsDataURL(f);
        })
    );
    Promise.all(readers).then((all) => set("images", [...(form.images || []), ...all].slice(0, 6)));
  };

  const removeImg = (idx) => set("images", form.images.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    const phone = String(form.phone || "").trim();
    if (!form.brand || !String(form.model).trim()) {
      toast.error("Please enter Make and Model.");
      return;
    }
    if (!form.price || parseInt(form.price, 10) <= 0) {
      toast.error("Please enter a valid Price.");
      return;
    }
    if (form.km_driven === "" || form.km_driven === null || parseInt(form.km_driven, 10) < 0) {
      toast.error("Please enter KM Driven.");
      return;
    }
    if (!String(form.location).trim()) {
      toast.error("Please select a Location.");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(phone)) {
      toast.error("Please enter a valid 10-digit Indian mobile number.");
      return;
    }
    if (!form.images || form.images.length === 0) {
      toast.error("Please upload at least 1 image.");
      return;
    }
    setSubmitting(true);
    try {
      await onSave({
        ...form,
        phone,
        year: parseInt(form.year, 10),
        km_driven: parseInt(form.km_driven, 10),
        price: parseInt(form.price, 10),
        owners: parseInt(form.owners, 10) || 1,
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 max-h-[75vh] overflow-y-auto pr-1" data-testid="car-form">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Brand <span className="text-red-500">*</span></Label>
          <Select value={form.brand} onValueChange={(v) => set("brand", v)}>
            <SelectTrigger data-testid="form-brand"><SelectValue /></SelectTrigger>
            <SelectContent>
              {BRANDS.map((b) => <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Model <span className="text-red-500">*</span></Label>
          <Input value={form.model} onChange={(e) => set("model", e.target.value)} placeholder="Swift VXI" required data-testid="form-model" />
        </div>
        <div>
          <Label>Year</Label>
          <Input type="number" value={form.year} onChange={(e) => set("year", e.target.value)} min={1990} max={2030} data-testid="form-year" />
        </div>
        <div>
          <Label>Color</Label>
          <Input value={form.color} onChange={(e) => set("color", e.target.value)} placeholder="White" data-testid="form-color" />
        </div>
        <div>
          <Label>Fuel</Label>
          <Select value={form.fuel_type} onValueChange={(v) => set("fuel_type", v)}>
            <SelectTrigger data-testid="form-fuel"><SelectValue /></SelectTrigger>
            <SelectContent>
              {FUEL_TYPES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Transmission</Label>
          <Select value={form.transmission} onValueChange={(v) => set("transmission", v)}>
            <SelectTrigger data-testid="form-transmission"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TRANSMISSIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>KM Driven <span className="text-red-500">*</span></Label>
          <Input type="number" value={form.km_driven} onChange={(e) => set("km_driven", e.target.value)} required data-testid="form-km" />
        </div>
        <div>
          <Label>Owners</Label>
          <Input type="number" value={form.owners} onChange={(e) => set("owners", e.target.value)} min={1} data-testid="form-owners" />
        </div>
        <div>
          <Label>Price (₹) <span className="text-red-500">*</span></Label>
          <Input type="number" value={form.price} onChange={(e) => set("price", e.target.value)} required data-testid="form-price" />
        </div>
        <div>
          <Label>Location <span className="text-red-500">*</span></Label>
          <Select value={form.location} onValueChange={(v) => set("location", v)}>
            <SelectTrigger data-testid="form-location"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label>Contact Phone (10-digit) <span className="text-red-500">*</span></Label>
          <Input
            type="tel"
            inputMode="numeric"
            maxLength={10}
            pattern="[6-9][0-9]{9}"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="98XXXXXXXX"
            required
            data-testid="form-phone"
          />
          <div className="text-[11px] text-[#718096] mt-1">Buyers will contact you on this number via call or WhatsApp.</div>
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Tell buyers what makes your car special…" data-testid="form-description" />
      </div>
      <div>
        <Label>Photos (up to 6) <span className="text-red-500">*</span></Label>
        <Input type="file" accept="image/*" multiple onChange={handleFiles} data-testid="form-images" />
        {form.images?.length > 0 && (
          <div className="mt-3 grid grid-cols-4 gap-2">
            {form.images.map((src, i) => (
              <div key={`${src.slice(0, 32)}-${i}`} className="relative aspect-square rounded-md overflow-hidden border border-[#0A192F]/10">
                <img src={src} alt="upl" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImg(i)} className="absolute top-1 right-1 bg-white/90 rounded-full p-1">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <Button type="submit" disabled={submitting} className="w-full bg-[#0A192F] hover:bg-[#112240] text-white" data-testid="form-submit-btn">
        {submitting ? "Saving…" : "Save listing"}
      </Button>
    </form>
  );
}

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white border border-[#0A192F]/10 rounded-xl p-5 gj-shadow-card">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-[0.18em] text-[#718096] font-semibold">{label}</div>
      <Icon className={`w-4 h-4 ${accent || "text-[#F97316]"}`} />
    </div>
    <div className="font-heading text-3xl font-extrabold text-[#0A192F] mt-2">{value}</div>
  </div>
);

const STATUS_BADGE = {
  pending: "bg-orange-100 text-orange-800 border-orange-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
};

export default function DealerDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [cars, setCars] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, enquiries: 0 });
  const [editing, setEditing] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.id) {
      nav("/login", { state: { from: "/dealer" } });
      return;
    }
    if (user.role !== "dealer" && user.role !== "admin") {
      toast.error("Dealer access required.");
      nav("/");
    }
  }, [user, loading, nav]);

  const refresh = useCallback(async () => {
    try {
      const [c, e, s] = await Promise.all([
        api.get("/dealer/cars"),
        api.get("/enquiries"),
        api.get("/dealer/stats"),
      ]);
      setCars(c.data);
      setEnquiries(e.data);
      setStats(s.data);
    } catch (err) {
      // 401 expected before auth completes — log others
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("[dealer] refresh failed:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (user && user.id) refresh();
  }, [user, refresh]);

  const onSave = async (data) => {
    try {
      if (editing) {
        await api.patch(`/cars/${editing.id}`, data);
        toast.success("Listing updated");
      } else {
        await api.post("/cars", data);
        toast.success("Listing submitted for approval");
      }
      setEditing(null);
      setOpenDialog(false);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await api.delete(`/cars/${id}`);
      toast.success("Listing deleted");
      refresh();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (loading || !user || !user.id) return <div className="p-20 text-center">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-10" data-testid="dealer-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="gj-divider-gold mb-3" />
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#0A192F]">
            Hello, {user.name?.split(" ")[0]}
          </h1>
          <p className="text-[#4A5568] mt-1 text-sm">
            {user.business_name || "Dealer dashboard"} · Manage inventory & enquiries
          </p>
        </div>
        <Dialog open={openDialog} onOpenChange={(o) => { setOpenDialog(o); if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#F97316] hover:bg-[#EA580C] text-[#0A192F] font-bold" data-testid="add-car-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Car
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading">{editing ? "Edit listing" : "Add a new car"}</DialogTitle>
            </DialogHeader>
            <CarForm initial={editing} onSave={onSave} onClose={() => { setOpenDialog(false); setEditing(null); }} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard icon={Car} label="Total Listings" value={stats.total} />
        <StatCard icon={CheckCircle2} label="Approved" value={stats.approved} />
        <StatCard icon={Clock} label="Pending" value={stats.pending} />
        <StatCard icon={Inbox} label="Enquiries" value={stats.enquiries} />
      </div>

      <Tabs defaultValue="listings" className="w-full">
        <TabsList className="bg-white border border-[#0A192F]/10">
          <TabsTrigger value="listings" data-testid="tab-listings">My Listings</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries</TabsTrigger>
        </TabsList>

        <TabsContent value="listings" className="mt-6">
          <div className="bg-white border border-[#0A192F]/10 rounded-xl overflow-hidden">
            {cars.length === 0 ? (
              <div className="p-12 text-center text-[#4A5568]" data-testid="empty-listings">
                You have no listings yet. Click <strong>Add Car</strong> to create your first.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F3F0EA]/40">
                    <TableHead>Car</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>KM</TableHead>
                    <TableHead>Price</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cars.map((c) => (
                    <TableRow key={c.id} data-testid={`listing-row-${c.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded bg-[#F3F0EA] overflow-hidden shrink-0">
                            {c.images?.[0] && <img src={c.images[0]} alt="" className="w-full h-full object-cover" />}
                          </div>
                          <div>
                            <div className="font-semibold text-[#0A192F]">{c.brand} {c.model}</div>
                            <div className="text-xs text-[#718096]">{c.location}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{c.year}</TableCell>
                      <TableCell>{KM(c.km_driven)}</TableCell>
                      <TableCell className="font-bold">{INR(c.price)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={STATUS_BADGE[c.status] || ""}>
                          {c.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setEditing(c); setOpenDialog(true); }}
                          data-testid={`edit-car-${c.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDelete(c.id)}
                          data-testid={`delete-car-${c.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-[#9B2C2C]" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="enquiries" className="mt-6">
          <div className="bg-white border border-[#0A192F]/10 rounded-xl overflow-hidden">
            {enquiries.length === 0 ? (
              <div className="p-12 text-center text-[#4A5568]">No enquiries yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F3F0EA]/40">
                    <TableHead>Car</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enquiries.map((e) => (
                    <TableRow key={e.id} data-testid={`enquiry-row-${e.id}`}>
                      <TableCell className="font-medium text-[#0A192F]">{e.car_label}</TableCell>
                      <TableCell>{e.name}</TableCell>
                      <TableCell>{e.phone}</TableCell>
                      <TableCell className="max-w-xs truncate text-[#4A5568]">{e.message || "—"}</TableCell>
                      <TableCell className="text-right">
                        <a href={`tel:${e.phone}`} className="inline-flex items-center text-sm font-semibold text-[#0A192F] hover:text-[#F97316]">
                          <Phone className="w-4 h-4 mr-1" /> Call
                        </a>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
